import { memo } from 'react';
import SelectableChip from '../../SelectableChip.jsx';
import FilterSlideoutSection from '../FilterSlideoutSection.jsx';
import FilterSlideoutOptionRow from '../FilterSlideoutOptionRow.jsx';
import { FILTER_SLIDEOUT_CHIP_LIST } from '../filterSlideoutTokens.js';
import {
  clearSection,
  getSectionActiveCount,
  includesValue,
  toggleArrayValue,
} from '../filterSlideout.utils.js';

const DEFAULT_ALERT_LEVEL_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'has-insights', label: 'Has insights' },
  { value: 'no-insights', label: 'No insights' },
];

const AI_TAG_CHIP_COLORED = [
  '!border-[var(--filter-slideout-tag-color)] !text-[var(--filter-slideout-tag-color)]',
  'dark:!border-[var(--filter-slideout-tag-color)] dark:!text-[var(--filter-slideout-tag-color)]',
].join(' ');

const AI_TAG_CHIP_COLORED_ACTIVE = [
  '!bg-[color-mix(in_srgb,var(--filter-slideout-tag-color)_12%,white)]',
  'dark:!bg-[color-mix(in_srgb,var(--filter-slideout-tag-color)_24%,transparent)]',
].join(' ');

function getTagChipStyle(tag) {
  return tag.color ? { '--filter-slideout-tag-color': tag.color } : undefined;
}

const AiInsightsSection = ({ section }) => {
  if (section.unavailable) return null;

  const activeCount = getSectionActiveCount(section);
  const value = section.value ?? {};
  const tags = value.tags ?? [];
  const alertLevel = value.alertLevel ?? section.allValue ?? 'any';
  const alertOptions = section.alertOptions ?? DEFAULT_ALERT_LEVEL_OPTIONS;

  const handleTagToggle = (tag) => {
    section.onChange?.({
      ...value,
      tags: toggleArrayValue(tags, tag.value),
    });
  };

  const handleAlertLevelSelect = (option) => {
    if (option.disabled || section.disabled) return;
    section.onChange?.({ ...value, tags, alertLevel: option.value });
  };

  return (
    <FilterSlideoutSection
      title={section.title}
      defaultExpanded={section.defaultExpanded ?? true}
      disabled={section.disabled}
      activeCount={activeCount}
      onClear={() => clearSection(section)}
    >
      <div className="flex flex-col gap-3">
        {section.tags?.length ? (
          <div className={FILTER_SLIDEOUT_CHIP_LIST}>
            {section.tags.map((tag) => {
              const selected = includesValue(tags, tag.value);
              return (
                <span
                  key={tag.value}
                  style={getTagChipStyle(tag)}
                  className="inline-flex"
                >
                  <SelectableChip
                    selected={selected}
                    disabled={section.disabled || tag.disabled}
                    size="s"
                    className={[
                      'max-w-[120px] overflow-hidden',
                      tag.color ? AI_TAG_CHIP_COLORED : '',
                      selected && tag.color ? AI_TAG_CHIP_COLORED_ACTIVE : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleTagToggle(tag)}
                  >
                    <span className="truncate">{tag.label}</span>
                  </SelectableChip>
                </span>
              );
            })}
          </div>
        ) : section.unavailableText ? (
          <p className="text-xs font-medium leading-5 text-content-muted">
            {section.unavailableText}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase leading-none tracking-[0.04em] text-content-muted">
            {section.alertLabel ?? 'Alert level'}
          </p>
          <div
            className="grid grid-cols-3 gap-2 [&>button]:h-8 [&>button]:text-xs [&>button]:font-medium"
            role="radiogroup"
            aria-label={section.alertLabel ?? 'Alert level'}
          >
            {alertOptions.map((option) => (
              <FilterSlideoutOptionRow
                key={option.value}
                control="radio"
                checked={alertLevel === option.value}
                disabled={section.disabled || option.disabled}
                label={option.label}
                onToggle={() => handleAlertLevelSelect(option)}
              />
            ))}
          </div>
        </div>
      </div>
    </FilterSlideoutSection>
  );
};

export default memo(AiInsightsSection);
